import { Card, CardContent, CardHeader, CardTitle } from '../ui/card.tsx';
import { Button } from '../ui/button.tsx';
import { Input } from '../ui/input.tsx';
import { Label } from '../ui/label.tsx';
import { Textarea } from '../ui/textarea.tsx';
import { Badge } from '../ui/badge.tsx';
import { Plus, Edit, Trash2, X, MessageSquare } from 'lucide-react';

interface AdminAnnouncement { 
    id: number; 
    Title: string; 
    Content: string; 
    Description: string; 
    Category: string; 
    PublishDate: string; 
    Image?: string | null;
}

interface AnnouncementsTabProps {
    announcements: AdminAnnouncement[];
    showAddForm: string | null;
    setShowAddForm: (value: string | null) => void;
    newAnnouncement: {
        title: string;
        description: string;
        content: string;
        category: string;
        date: string;
    };
    setNewAnnouncement: (value: any) => void;
    selectedFile: File | null;
    setSelectedFile: (file: File | null) => void;
    handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleSaveAnnouncement: (e: React.FormEvent) => Promise<void>;
    resetAnnouncementForm: () => void;
    handleEditAnnouncement: (ann: AdminAnnouncement) => void;
    handleDeleteAnnouncement: (id: number) => Promise<void>;
    editingId: number | null;
}

export function AnnouncementsTab({
    announcements,
    showAddForm,
    setShowAddForm,
    newAnnouncement,
    setNewAnnouncement,
    selectedFile,
    setSelectedFile,
    handleFileChange,
    handleSaveAnnouncement,
    resetAnnouncementForm,
    handleEditAnnouncement,
    handleDeleteAnnouncement,
    editingId
}: AnnouncementsTabProps) {
    return (
        <Card>
            <CardHeader className="flex flex-row justify-between items-center">
                <CardTitle>Duyurular</CardTitle>
                <Button 
                    onClick={() => { 
                        resetAnnouncementForm(); 
                        setShowAddForm('announcement'); 
                    }} 
                    className="gap-2"
                >
                    <Plus className="h-4 w-4" /> Yeni
                </Button>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* EKLEME / DÜZENLEME FORMU */}
                {showAddForm === 'announcement' && (
                    <Card className="bg-slate-50 border-2 border-orange-200">
                        <CardContent className="pt-6">
                            <form onSubmit={handleSaveAnnouncement} className="space-y-4">
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Başlık *</Label>
                                        <Input 
                                            value={newAnnouncement.title} 
                                            onChange={(e) => setNewAnnouncement({...newAnnouncement, title: e.target.value})} 
                                            required 
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Kategori</Label>
                                        <select 
                                            className="w-full p-2 border rounded" 
                                            value={newAnnouncement.category} 
                                            onChange={(e) => setNewAnnouncement({...newAnnouncement, category: e.target.value})}
                                        >
                                            <option value="GENEL">Genel</option>
                                            <option value="IS_ILANI">İş İlanı</option>
                                            <option value="HABER">Haber</option>
                                            <option value="VEFAT">Vefat</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Kısa Özet</Label>
                                    <Input 
                                        value={newAnnouncement.description} 
                                        onChange={(e) => setNewAnnouncement({...newAnnouncement, description: e.target.value})} 
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>İçerik *</Label>
                                    <Textarea 
                                        className="min-h-[100px]" 
                                        value={newAnnouncement.content} 
                                        onChange={(e) => setNewAnnouncement({...newAnnouncement, content: e.target.value})} 
                                        required 
                                    />
                                </div>
                                <div className="grid md:grid-cols-2 gap-4 items-end">
                                    <div className="space-y-2">
                                        <Label>Resim</Label>
                                        <div className="flex items-center gap-2">
                                            <Input 
                                                type="file" 
                                                accept="image/*" 
                                                onChange={handleFileChange} 
                                                className="bg-white"
                                            />
                                            {selectedFile && (
                                                <Button 
                                                    type="button" 
                                                    size="icon" 
                                                    variant="ghost" 
                                                    onClick={() => setSelectedFile(null)}
                                                >
                                                    <X className="h-4 w-4"/>
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Tarih</Label>
                                        <Input 
                                            type="date" 
                                            value={newAnnouncement.date} 
                                            onChange={(e) => setNewAnnouncement({...newAnnouncement, date: e.target.value})} 
                                        />
                                    </div>
                                </div>
                                <div className="flex justify-end gap-2 pt-2">
                                    <Button type="button" variant="outline" onClick={resetAnnouncementForm}>
                                        İptal
                                    </Button>
                                    <Button type="submit" className="bg-orange-600 text-white">
                                        {editingId ? 'Güncelle' : 'Kaydet'}
                                    </Button>
                                </div>
                            </form>
                        </CardContent>
                    </Card>
                )}

                {/* LİSTELEME KISMI */}
                <div className="space-y-2">
                    {announcements.map((ann) => (
                        <div 
                            key={ann.id} 
                            className="flex items-center justify-between p-4 border rounded-lg bg-white hover:bg-slate-50 transition-colors"
                        >
                            <div className="flex items-center gap-4">
                                {ann.Image ? (
                                    <div className="w-16 h-16 flex-shrink-0 border border-slate-200 rounded-md overflow-hidden">
                                        <img src={ann.Image} alt={ann.Title} className="w-full h-full object-cover" />
                                    </div>
                                ) : (
                                    <div className="w-16 h-16 flex-shrink-0 bg-slate-100 rounded-md flex items-center justify-center border border-slate-200">
                                        <MessageSquare className="h-6 w-6 text-slate-400" />
                                    </div>
                                )}
                                <div>
                                    <h3 className="font-bold text-lg text-slate-800 line-clamp-1">{ann.Title}</h3>
                                    <div className="flex items-center gap-2 mt-1">
                                        <Badge variant="outline" className="text-xs">{ann.Category}</Badge>
                                        <p className="text-xs text-slate-500">
                                            {new Date(ann.PublishDate).toLocaleDateString()}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="flex gap-2">
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => handleEditAnnouncement(ann)} 
                                    className="text-blue-500 hover:bg-blue-50"
                                >
                                    <Edit className="h-4 w-4" />
                                </Button>
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => handleDeleteAnnouncement(ann.id)} 
                                    className="text-red-500 hover:bg-red-50"
                                >
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            </CardContent>
        </Card>
    );
}
